import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { designTokens } from '@course-authoring/shared-types';
import {
  TagConflictError,
  useCreateTagMutation,
  useDeleteTagMutation,
  useRenameTagMutation,
  useTagsQuery,
  type TagSort,
  type TagSortOrder,
  type TagUsage,
} from '../api/tagApi';
import { DeleteTagModal } from '../components/DeleteTagModal';
import { SortableHeader } from '../components/table/SortableHeader';
import { SEARCH_DEBOUNCE_MS } from '../hooks/useListControls';
import {
  AdminPageHeader,
  AdminStatusMessage,
  adminFocusable,
  adminTokens,
  createAdminListStyles,
  useAdminNarrowViewport,
} from '../admin-ui';

const DEFAULT_SORT: TagSort = 'name';
const DEFAULT_ORDER: TagSortOrder = 'asc';
const ACTIONS_COLUMN_WIDTH = '190px';

const countLabel = (count: number, singular: string, pluralWord = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : pluralWord}`;

// A duplicate-name conflict names the exact value that was typed (matching the
// "already applied" copy the picker uses) rather than the generic message the
// server returns — that generic text is a fallback for the response shape,
// not the copy this screen should show for the one case it can identify.
const formatTagSubmitError = (error: unknown, attemptedName: string, fallback: string): string => {
  if (error instanceof TagConflictError) return `"${attemptedName}" already exists.`;
  return error instanceof Error ? error.message : fallback;
};

export function TagsPage() {
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createAdminListStyles({ narrow: isNarrow }), [isNarrow]);

  const [searchParams, setSearchParams] = useSearchParams();
  // setSearchParams' identity changes with the URL (see useListControls.ts);
  // route through a ref so setSearchInput/handleSort stay referentially stable.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const urlSearch = searchParams.get('q') ?? '';
  const sort: TagSort = searchParams.get('sort') === 'usage' ? 'usage' : DEFAULT_SORT;
  const order: TagSortOrder = searchParams.get('dir') === 'desc' ? 'desc' : DEFAULT_ORDER;
  // Read-only by default (per design doc) — editing is a URL-hidden capability,
  // not a role: there's no "who can manage tags" permission in the system,
  // just this param left out of the links we give to people who shouldn't add tags.
  const canManage = searchParams.get('manage') === '1';

  // Debounced independently of the URL: typing updates this input immediately,
  // then flushes to the URL (and triggers a refetch) after SEARCH_DEBOUNCE_MS —
  // same rhythm as the paginated list pages (useListControls), just applied
  // locally since this list has no pagination to coordinate with.
  const [searchInput, setSearchInput] = useState(urlSearch);
  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput === urlSearch) return;
      setSearchParamsRef.current((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('q', searchInput); else next.delete('q');
        return next;
      }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const handleSort = useCallback((key: TagSort) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const isActive = (prev.get('sort') === 'usage' ? 'usage' : DEFAULT_SORT) === key;
      const currentDir: TagSortOrder = prev.get('dir') === 'desc' ? 'desc' : DEFAULT_ORDER;
      const nextDir: TagSortOrder = isActive ? (currentDir === 'asc' ? 'desc' : 'asc') : DEFAULT_ORDER;
      if (key === DEFAULT_SORT) next.delete('sort'); else next.set('sort', key);
      if (nextDir === DEFAULT_ORDER) next.delete('dir'); else next.set('dir', nextDir);
      return next;
    }, { replace: true });
  }, []);

  const { data, isLoading, isError } = useTagsQuery({ search: urlSearch, sort, order });
  const items = data?.items ?? [];
  // Independent of the table's own search filter above — the "Similar to
  // existing" advisory while typing a new tag name has to check the whole
  // library, not just whatever's currently filtered into view.
  const { data: fullLibrary } = useTagsQuery();

  const createMutation = useCreateTagMutation();
  const renameMutation = useRenameTagMutation();
  const deleteMutation = useDeleteTagMutation();

  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [addTagError, setAddTagError] = useState<string | null>(null);

  const similarExistingTagNames = useMemo(() => {
    const needle = newTagName.trim().toLowerCase();
    // A single character matches too much of the library to be a useful nudge
    // (per design: the advisory only starts once a second character is typed).
    if (needle.length < 2) return [];
    return (fullLibrary?.items ?? [])
      .filter((tag) => tag.name.toLowerCase() !== needle && tag.name.toLowerCase().includes(needle))
      .map((tag) => tag.name);
  }, [fullLibrary, newTagName]);

  const [renamingTagId, setRenamingTagId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TagUsage | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // manage=1 dropping out from under an open row (back/forward nav, a hand-edited
  // URL) should close editing immediately rather than leave it reachable.
  useEffect(() => {
    if (canManage) return;
    setIsAddingTag(false);
    setRenamingTagId(null);
    setDeleteTarget(null);
  }, [canManage]);

  // Only one inline edit row (add or rename) is ever open at a time — starting
  // one closes the other rather than stacking both in the table at once.
  const handleStartAdd = useCallback(() => {
    setIsAddingTag(true);
    setNewTagName('');
    setAddTagError(null);
    setRenamingTagId(null);
    setRenameError(null);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAddingTag(false);
    setNewTagName('');
    setAddTagError(null);
  }, []);

  const handleSubmitAdd = useCallback(() => {
    const name = newTagName.trim();
    if (!name) {
      setAddTagError('Tag name is required');
      return;
    }
    createMutation.mutate(name, {
      onSuccess: () => {
        setIsAddingTag(false);
        setNewTagName('');
        setAddTagError(null);
      },
      onError: (error) => setAddTagError(formatTagSubmitError(error, name, 'Failed to create tag')),
    });
  }, [createMutation, newTagName]);

  const handleStartRename = useCallback((tag: TagUsage) => {
    setRenamingTagId(tag.id);
    setRenameValue(tag.name);
    setRenameError(null);
    setIsAddingTag(false);
    setNewTagName('');
    setAddTagError(null);
  }, []);

  const handleCancelRename = useCallback(() => {
    setRenamingTagId(null);
    setRenameError(null);
  }, []);

  const handleSubmitRename = useCallback((id: number) => {
    const name = renameValue.trim();
    if (!name) {
      setRenameError('Tag name is required');
      return;
    }
    renameMutation.mutate({ id, name }, {
      onSuccess: () => {
        setRenamingTagId(null);
        setRenameError(null);
      },
      onError: (error) => setRenameError(formatTagSubmitError(error, name, 'Failed to rename tag')),
    });
  }, [renameMutation, renameValue]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        setDeleteError(null);
      },
      onError: (error) => setDeleteError(error instanceof Error ? error.message : 'Failed to delete tag'),
    });
  }, [deleteMutation, deleteTarget]);

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  return (
    <div style={styles.page}>
      <AdminPageHeader
        title="Tags"
        description="Labels for organizing and filtering lessons and courses."
        actions={canManage && (
          <button
            type="button"
            style={{ ...styles.addLessonButton, ...(isAddingTag ? styles.confirmDeleteButtonDisabled : {}) }}
            onClick={handleStartAdd}
            disabled={isAddingTag}
          >
            + New tag
          </button>
        )}
      />

      <div style={styles.listToolbar}>
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search tags..."
          style={styles.filterInput}
          aria-label="Search tags"
        />
        <span style={styles.resultsCount}>
          {countLabel(items.length, 'tag')}
        </span>
      </div>

      <section style={styles.panel}>
        {isLoading ? (
          <p style={styles.stateText}>Loading tags...</p>
        ) : isError ? (
          <div style={{ padding: adminTokens.spacing.xl }}>
            <AdminStatusMessage tone="error" role={null}>Failed to load tags.</AdminStatusMessage>
          </div>
        ) : items.length === 0 && !isAddingTag ? (
          <p style={styles.stateText}>
            {urlSearch ? 'No tags match your search.' : 'No tags yet.'}
          </p>
        ) : (
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <SortableHeader
                    label="Tag"
                    sortKey="name"
                    activeKey={sort}
                    direction={order}
                    onSort={handleSort}
                    styles={styles}
                  />
                  <SortableHeader
                    label="Used by"
                    sortKey="usage"
                    activeKey={sort}
                    direction={order}
                    onSort={handleSort}
                    styles={styles}
                    headerStyle={{ textAlign: 'right' }}
                  />
                  {canManage && (
                    <th style={{ ...styles.headerCellActions, width: ACTIONS_COLUMN_WIDTH }} aria-hidden="true" />
                  )}
                </tr>
              </thead>
              <tbody>
                {canManage && isAddingTag && (
                  <tr style={styles.tableRow}>
                    <td colSpan={3} style={styles.bodyCell}>
                      <div style={inlineEditRowStyle()}>
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(event) => {
                            setNewTagName(event.target.value);
                            setAddTagError(null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') handleSubmitAdd();
                            if (event.key === 'Escape') handleCancelAdd();
                          }}
                          placeholder="New tag name"
                          aria-label="New tag name"
                          style={inlineEditInputStyle()}
                          autoFocus
                          disabled={createMutation.isPending}
                        />
                        <InlineEditButton onClick={handleSubmitAdd} disabled={createMutation.isPending}>
                          {createMutation.isPending ? 'Adding...' : 'Add tag'}
                        </InlineEditButton>
                        <InlineEditButton onClick={handleCancelAdd} disabled={createMutation.isPending}>
                          Cancel
                        </InlineEditButton>
                      </div>
                      {addTagError && <p style={inlineEditErrorStyle()}>{addTagError}</p>}
                      {similarExistingTagNames.length > 0 && (
                        <p style={advisoryNoticeStyle}>Similar to existing: {similarExistingTagNames.join(', ')}</p>
                      )}
                    </td>
                  </tr>
                )}
                {items.map((tag) => (
                  canManage && renamingTagId === tag.id ? (
                    <tr key={tag.id} style={styles.tableRow}>
                      <td colSpan={3} style={styles.bodyCell}>
                        <div style={inlineEditRowStyle()}>
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(event) => {
                              setRenameValue(event.target.value);
                              setRenameError(null);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') handleSubmitRename(tag.id);
                              if (event.key === 'Escape') handleCancelRename();
                            }}
                            aria-label={`Rename ${tag.name}`}
                            style={inlineEditInputStyle()}
                            autoFocus
                            disabled={renameMutation.isPending}
                          />
                          <InlineEditButton

                            onClick={() => handleSubmitRename(tag.id)}
                            disabled={renameMutation.isPending}
                          >
                            {renameMutation.isPending ? 'Saving...' : 'Save'}
                          </InlineEditButton>
                          <InlineEditButton onClick={handleCancelRename} disabled={renameMutation.isPending}>
                            Cancel
                          </InlineEditButton>
                        </div>
                        {renameError && <p style={inlineEditErrorStyle()}>{renameError}</p>}
                        <p style={advisoryNoticeStyle}>
                          Renaming updates this tag on {countLabel(tag.totalCount, 'item')}.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <tr key={tag.id} style={styles.tableRow}>
                      <td style={styles.bodyCell}>{tag.name}</td>
                      <td style={{ ...styles.bodyCell, textAlign: 'right' }}>
                        {tag.totalCount === 0 ? (
                          <div style={{ color: adminTokens.text.secondary }}>Not used</div>
                        ) : (
                          <>
                            <div>{countLabel(tag.totalCount, 'item')}</div>
                            <div style={{ fontSize: '12px', color: adminTokens.text.secondary, marginTop: '2px' }}>
                              {countLabel(tag.lessonCount, 'lesson')}, {countLabel(tag.courseCount, 'course')}
                            </div>
                          </>
                        )}
                      </td>
                      {canManage && (
                        <td style={{ ...styles.actionsCell, width: ACTIONS_COLUMN_WIDTH }}>
                          <div style={actionsGroupStyle()}>
                            <TagRowActionButton
                              variant="neutral"

                              onClick={() => handleStartRename(tag)}
                            >
                              Rename
                            </TagRowActionButton>
                            <TagRowActionButton
                              variant="danger"

                              onClick={() => {
                                setDeleteTarget(tag);
                                setDeleteError(null);
                              }}
                            >
                              Delete
                            </TagRowActionButton>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <DeleteTagModal
        isOpen={canManage && deleteTarget !== null}
        tagName={deleteTarget?.name ?? ''}
        lessonCount={deleteTarget?.lessonCount ?? 0}
        courseCount={deleteTarget?.courseCount ?? 0}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

function inlineEditRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: adminTokens.spacing.sm,
    flexWrap: 'wrap' as const,
  };
}

function inlineEditInputStyle() {
  return {
    flex: '1 1 240px',
    minWidth: '160px',
    maxWidth: '360px',
    height: '38px',
    padding: '0 12px',
    fontSize: adminTokens.typography.body.size,
    fontFamily: 'inherit',
    // The 2 px focus-colored border is the active-edit indicator (the field is
    // focused on open), so no separate outline.
    border: `2px solid ${adminTokens.focus.ring}`,
    borderRadius: adminTokens.radius.control,
    outline: 'none',
    backgroundColor: adminTokens.surface.panel,
    color: adminTokens.text.primary,
    boxSizing: 'border-box' as const,
  };
}

interface InlineEditButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

// Add tag / Cancel / Save — hover only darkens the border, no fill, per design.
function InlineEditButton({ onClick, disabled = false, children }: InlineEditButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  const style: CSSProperties = {
    ...adminFocusable,
    minHeight: '36px',
    padding: '0 16px',
    fontFamily: 'inherit',
    fontSize: adminTokens.typography.body.size,
    fontWeight: 600,
    color: adminTokens.text.primary,
    backgroundColor: adminTokens.surface.panel,
    border: `1px solid ${isHovered && !disabled ? adminTokens.text.primary : adminTokens.border.control}`,
    borderRadius: adminTokens.radius.control,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
  };

  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </button>
  );
}

interface TagRowActionButtonProps {
  variant: 'neutral' | 'danger';
  onClick: () => void;
  children: ReactNode;
}

// Hover is driven by React state rather than mutating event.currentTarget.style
// directly — that approach left the fill reset by the next re-render (e.g. a
// background refetch) while the border edit happened to survive, so "hover"
// looked half-applied. Danger colors come from the static `designTokens`
// constant, never an account-themed value; neutral uses the admin palette.
// Same compact sizing as Edit/Delete on Users & Permissions either way.
function TagRowActionButton({ variant, onClick, children }: TagRowActionButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isDanger = variant === 'danger';

  const style: CSSProperties = {
    ...adminFocusable,
    minHeight: '30px',
    padding: '0 10px',
    fontFamily: 'inherit',
    fontSize: adminTokens.typography.label.size,
    fontWeight: 600,
    borderRadius: adminTokens.radius.control,
    cursor: 'pointer',
    color: isDanger ? designTokens.colors.errorText : adminTokens.text.primary,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: isDanger
      ? (isHovered ? designTokens.colors.errorBorder : designTokens.colors.errorText)
      : (isHovered ? adminTokens.text.primary : adminTokens.border.control),
    // Rename is border-only on hover — only Delete gets the filled background.
    backgroundColor: isDanger && isHovered ? designTokens.colors.errorBackground : 'transparent',
  };

  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </button>
  );
}

function actionsGroupStyle() {
  return {
    display: 'inline-flex',
    gap: adminTokens.spacing.sm,
    justifyContent: 'flex-end' as const,
  };
}

// No design token for a warning tone — this note is informational (unlike the
// delete modal's destructive-red notice), so it gets its own quiet amber.
// Shared by the rename-impact note and the "Similar to existing" advisory.
const advisoryNoticeStyle = {
  margin: '8px 0 0 0',
  fontSize: adminTokens.typography.label.size,
  color: adminTokens.status.warning.text,
};

function inlineEditErrorStyle() {
  return {
    margin: '8px 0 0 0',
    fontSize: adminTokens.typography.label.size,
    color: adminTokens.status.error.text,
  };
}
